import React, { useCallback, useState } from 'react';
import { CardSummary, ATTRIBUTE_ICONS, ATTRIBUTE_NAMES } from '../../types/cards';
import './Card.css';

interface CardProps {
  card: CardSummary;
  onClick?: () => void;
  showBack?: boolean;
  isFlipped?: boolean;
  onFlip?: () => void;
  flipOnClick?: boolean;
}

function CardComponent({ 
  card, 
  onClick,
  showBack = false,
  isFlipped = false,
  onFlip,
  flipOnClick = false
}: CardProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleClick = useCallback(() => {
    if (flipOnClick) {
      onFlip?.();
    }
    onClick?.();
  }, [flipOnClick, onFlip, onClick]);

  const handleImageError = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
    // 에러 발생 시 대체 이미지로 전환
    const img = event.currentTarget;
    if (!imageError) {
      setImageError(true);
      // 대체 이미지로 시도 (뒷면 이미지 사용)
      img.src = '/backcard.png';
      // 대체 이미지도 실패하면 숨김
      img.onerror = () => {
        img.style.display = 'none';
      };
    } else {
      // 대체 이미지도 실패한 경우
      img.style.display = 'none';
    }
  }, [imageError]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  return (
    <article 
      className={`card card--${card.grade.toLowerCase()} ${isFlipped ? 'card--flipped' : ''}`}
      onClick={handleClick}
    >
      <div className="card__inner">
        {/* 카드 앞면 */}
        <div className="card__front">
          <div className="card__image-wrapper">
            {!imageError && (
              <img
                src={card.image_url}
                alt={card.name}
                loading="lazy"
                onError={handleImageError}
                onLoad={handleImageLoad}
                style={{ opacity: imageLoaded ? 1 : 0, transition: 'opacity 0.3s' }}
              />
            )}
            {imageError && (
              <div className="card__image-placeholder">
                <span>이미지 없음</span>
              </div>
            )}
            <span className="card__grade">{card.grade}</span>
            {showBack && (
              <div className="card__flip-hint">클릭하여 뒤집기</div>
            )}
          </div>
          {/* 카드 이름 제거 - 상세 모달에서만 표시 */}
          {/* 전투 속성 영역 */}
          {(card.cost !== undefined || card.attack !== undefined || card.defense !== undefined || card.hp !== undefined || card.attribute) && (
            <div className="card__stats">
              {card.attribute && (
                <div className="card__attribute" title={ATTRIBUTE_NAMES[card.attribute]}>
                  <span className="card__attribute-icon">{ATTRIBUTE_ICONS[card.attribute]}</span>
                  <span className="card__attribute-name">{ATTRIBUTE_NAMES[card.attribute]}</span>
                </div>
              )}
              <div className="card__stats-row">
                {card.cost !== undefined && (
                  <div className="card__stat-item" title="코스트">
                    <span className="card__stat-icon">⚡</span>
                    <span className="card__stat-value">{card.cost}</span>
                  </div>
                )}
                {card.attack !== undefined && (
                  <div className="card__stat-item" title="공격력">
                    <span className="card__stat-icon">⚔️</span>
                    <span className="card__stat-value">{card.attack}</span>
                  </div>
                )}
                {card.defense !== undefined && (
                  <div className="card__stat-item" title="방어력">
                    <span className="card__stat-icon">🛡️</span>
                    <span className="card__stat-value">{card.defense}</span>
                  </div>
                )}
                {card.hp !== undefined && (
                  <div className="card__stat-item" title="체력">
                    <span className="card__stat-icon">❤️</span>
                    <span className="card__stat-value">{card.hp}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 카드 뒷면 */}
        {showBack && (
          <div className="card__back">
            <img 
              src="/backcard.png" 
              alt="카드 뒷면"
              className="card__back-image"
              loading="lazy"
            />
          </div>
        )}
      </div>
    </article>
  );
}

// React.memo로 불필요한 리렌더링 방지
export const Card = React.memo(CardComponent, (prevProps, nextProps) => {
  // 카드 ID가 같고, 주요 props가 변경되지 않았으면 리렌더링 방지
  return (
    prevProps.card.id === nextProps.card.id &&
    prevProps.showBack === nextProps.showBack &&
    prevProps.isFlipped === nextProps.isFlipped
  );
});

